import { useState } from 'react';
import {
  Button,
  Box,
  useMediaQuery,
  queryHelpers,
  ParagraphText,
} from '@leagueplatform/genesis-core';
import { useIntl } from '@leagueplatform/locales';
import {
  ANALYTICS_SCREEN_NAMES,
  FullScreenContainer,
  SUB_PRODUCT_AREA_NAMES,
  DETAIL,
} from '@telus-alberta-health-web/shared-module';
import { useAcceptUserTerms } from 'hooks/use-accept-user-terms';
import { OnboardingFlowContainer } from 'components/onboarding-flow-container/onboarding-flow-container';
import { captureError } from '@leagueplatform/observability';
import { EVENT_NAME, trackAnalyticsEvent } from '@leagueplatform/analytics';
import { TOAST_STATUS, toastActions } from '@leagueplatform/toast-messages';
import { SECONDARY_CONTENT_MAX_WIDTH } from 'common/constants';
import { PrivacyPolicyRendererProps } from 'types/types';
import { RichTextSection } from 'components/rich-text-renderer/rich-text-section.component';

export const PrivacyPolicyRenderer = ({
  currentConsentDisclaimer,
  currentConsentVersion,
}: PrivacyPolicyRendererProps) => {
  const { formatMessage } = useIntl();
  const [loading, setLoading] = useState(false);

  const isMobile = useMediaQuery(queryHelpers.down('mobileLandscape'));

  const acceptTermsMutation = useAcceptUserTerms();

  const handleAcceptTerms = async () => {
    setLoading(true);
    try {
      await acceptTermsMutation(currentConsentVersion);

      trackAnalyticsEvent(EVENT_NAME.BUTTON_CLICKED, {
        screen_name: ANALYTICS_SCREEN_NAMES.PRIVACY_NOTICE,
        product_area: SUB_PRODUCT_AREA_NAMES.ACCOUNT_SETUP,
        detail: DETAIL.AGREE_AND_CONTINUE,
      });
    } catch (error) {
      captureError(new Error(`Failed to accept privacy policy: ${error}`));
      toastActions.add({
        type: TOAST_STATUS.ERROR,
        textId: 'STR_ERROR_CAPTION_ACCEPT',
      });
    } finally {
      setLoading(false);
    }
  };

  return (
    <FullScreenContainer
      css={{
        background: '$surfaceBackgroundSecondary',
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
      }}
    >
      <OnboardingFlowContainer
        css={{ maxWidth: SECONDARY_CONTENT_MAX_WIDTH }}
        isMobile={isMobile}
      >
        <RichTextSection>{currentConsentDisclaimer}</RichTextSection>
        <ParagraphText
          css={{
            marginTop: '$threeQuarters',
            typography: '$subtitleOne',
          }}
        >
          {formatMessage({ id: 'STR_AGREE_AND_CONTINUE_QUESTION' })}
        </ParagraphText>
        {!isMobile && (
          <Box
            css={{
              width: '100%',
              height: '1px',
              backgroundColor: '$onSurfaceBorderDefault',
              marginTop: '$twoAndHalf',
              marginBottom: '$one',
            }}
          />
        )}
        <Button
          loading={loading}
          css={{
            alignSelf: 'flex-end',
            width: isMobile ? '90%' : '',
            position: isMobile ? 'relative' : '',
            right: isMobile ? '$one' : '',
            justifyContent: isMobile ? 'center' : '',
            marginBlockStart: isMobile ? '$oneAndHalf' : '',
            marginBlockEnd: isMobile ? '$twoAndHalf' : '',
          }}
          onClick={handleAcceptTerms}
        >
          {formatMessage({ id: 'STR_AGREE_AND_CONTINUE' })}
        </Button>
      </OnboardingFlowContainer>
    </FullScreenContainer>
  );
};
